import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import { useI18nContext } from "@/hooks/useI18nContext";
import { FAQ_HREF, PAGES } from "@/shared/constants/common.constants";
import { FIRST_PROMOTER_KEY, FIRST_PROMOTER_KEY_US } from "@/shared/constants/env/externalService.constants";
import { ROOT_HOST } from "@/shared/constants/env/internalService.constants";
import { NavbarDataType } from "@/shared/data/navbar.data";
import RatingSchema from "@/shared/store/linkingData/RatingSchema";
import useThemeStore from "@/shared/store/useThemeStore";
import { isUceCountryCode } from "@/shared/utils/route.util";
import { getIsSuccessPage } from "@/shared/utils/url.util";
import Head from "next/head";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import FeaturesGrid from "../Home/FeaturesGrid/FeaturesGrid";
import GraphyAssist from "../Home/GraphyAssist";
import FeatureSection from "../PricingPage/FeatureSection";
import PricingComparisonSection from "../PricingPage/PricingComparisonSection";
import ScheduleCallSection from "../PricingPage/ScheduleCall";
import CentreCtaSection from "../ProductPage/CentreCtaSection";
import ProductStepsSection from "../ProductPage/ProductStepsSection";
import ResultsSection from "../ProductPage/ResultsSection";
import { ButtonSizeEnum, ButtonTypeEnum } from "../atoms/Button";
import MainLayout from "../layouts/MainLayout/MainLayout";
import { CtaActionType } from "../molecules/CtaButtonGroup";
import ExitIntentForm from "../molecules/ExitIntentForm/ExitIntentForm";
import MarqueeCarousel from "../organisms/Carousels/MarqueeCarousel";
import SuccessStoriesMarqueeSection from "../organisms/Carousels/SuccessStoriesMarqueeSection";
import FaqSection from "../organisms/FaqSection";
import { FooterDataType } from "../organisms/Footer";
import InfoAccordion from "../organisms/InfoAccordion/InfoAccordion";
import OtherOfferingsSection from "../organisms/OtherOfferingsSection";
import OtherOfferingsSectionUS from "../organisms/OtherOfferingsSectionUS";
import ComparisonFeatures from "../templates/LandingPage/ComparisonFeatures";
import ComparisonTable from "../templates/LandingPage/ComparisonTable";
import Feature from "../templates/LandingPage/Feature";
import HeroHeader from "../templates/LandingPage/HeroHeader";
import HomeTestimonials from "../templates/LandingPage/HomeTestimonials";
import MarketingHeading from "../templates/LandingPage/MarketingHeading";
import MarketingHeadingUs from "../templates/LandingPage/MarketingHeadingUs";
import MarqueeImages from "../templates/LandingPage/MarqueeImages";
import MultimediaGrid from "../templates/LandingPage/MultimediaGrid";
import ReviewsSlider from "../templates/LandingPage/ReviewsSlider";
import ComparisonFeaturesWithCta from "./ComparisonFeaturesWithCta";
import CreatorEstimationSection from "./CreatorEstimationSection";
import CreatorEstimationSectionUS from "./CreatorEstimationSectionUS";
import ErrorBoundary from "./ErrorBoundary";
import LevelUpSection from "./LevelUpSection";
import PlansComparisonTable from "./PlansComparisonTable";
import PricingCompareSection from "./PricingCompareSection";
import { PromotionStrip } from "./PromotionStrip";
import { PromotionStripUS } from "./PromotionStripUS";
import AboutCreator from "./WebinarComponents/AboutCreator";
import AdditionalBonus from "./WebinarComponents/AdditionalBonus";
import Certificate from "./WebinarComponents/Certificate";
import CreatorResult from "./WebinarComponents/CreatorResult";
import Roadmap from "./WebinarComponents/Roadmap";
import WebinarDetails, { WebinarDetailsProps } from "./WebinarComponents/WebinarDetails";
import WebinarIntent from "./WebinarComponents/WebinarIntent";
import WebinarRegistrationSuccess from "./WebinarComponents/WebinarRegistrationSuccess";
import WebinarTestimonial from "./WebinarComponents/WebinarTestimonial";
import { useExitIntent } from "./useExitIntent";
type CreateAnimatedLPProps = {
  pageSections: { [key: string]: any } | any;
  meta: { [key: string]: any } | any;
  scheduleDemoOnly: boolean;
  topSubscribedGraphers: [];
  langFile: string | undefined;
  options: object | any;
  navbarData: NavbarDataType;
  footerData: FooterDataType;
  locale?: string;
};

const CreateAnimatedLP = ({
  pageSections,
  meta,
  scheduleDemoOnly = false,
  topSubscribedGraphers = [],
  langFile: langFileSource = "common",
  options = {},
  navbarData,
  footerData,
  locale,
}: CreateAnimatedLPProps) => {
  const langFile = langFileSource;
  const showFooter = !(locale === "us");

  const path = usePathname();
  

  const noIndexMeta = (locale === "us" && path.includes("online-courses")) || path.includes("get-started-fbform") || path.includes("online-course-platform");
  const [countryCode, setCountryCode] = useState("US")
  const isUsLocale = useI18nContext().locale === "us";
  const isUCECountry = isUceCountryCode(countryCode);
  const isthinkfic = path.includes("thinkific-alternative/") && (isUsLocale || (isUCECountry && locale !== "en"));
  const [showExitModal, setShowExitModal] = useState(false);
  const exitIntentResult = useExitIntent();
  const shouldShowExitIntent = isthinkfic ? exitIntentResult : false;

  const [courseInfo, setCourseInfo] = useState({
    instructorImage: "",
    title: "",
    date: "",
    time: "",
    duration: "",
    language: ""
  });

  

  const {
    pageLink = "/store",
    title,
    description,
    amplitudeSrc: source,
    pageType = "root",
    marketingFooterTitle,
    marketFooterSubtitle,
    marketFooterCtaPromoText,
    marketFooterCtaText,
    marketingFooterHero,
    hideNavCta,
    customCta1Link,
    hideNavLinks,
    ogImage,
  } = meta || {};
  const router = useRouter();

  const isWebinarRebecca = router.asPath.includes("webinar-rebecca-schocker/");
  const isWebinarBrookeHarris = router.asPath.includes("webinar-brooke-harris/")
  const isWebinarSuccess = 'webinar_success' in router?.query;
  const isSuccessPage = getIsSuccessPage(router.query);


  const handeShowCTA = (section: { [key: string]: any }) => {
    const ctaVal = section?.showCTA;
    return ctaVal === false ? false : true;
  };

  const renderLPBody = () => {
    if (isSuccessPage) {
      return (
        <WebinarRegistrationSuccess
          courseInfo={courseInfo}
          ctas={[
            {
              type: ButtonTypeEnum.Primary,
              size: ButtonSizeEnum.Regular,
              text: "Click here to join the community",
              href: "https://www.facebook.com/groups/894352482620198",
              amplitudeEvent: {
                eventName: "CTA Clicked",
                eventProperties: { button: "Go to Dashboard" },
              },
            },
          ]}
        />
      );
    } else if (pageSections.length > 0) {
      return (
        <>
          {pageSections.map(
            (section: { [key: string]: string }, index: string) => (
              <React.Fragment key={`${langFile}-${section.type}-${index}`}>
                {renderComponent(section, index)}
              </React.Fragment>
            )
          )}
        </>
      );
    }

    return null;
  };

  function renderComponent(section: { [key: string]: any }, index: string) {
        
    
    switch (section.type) {
      case "hero-header":
        return (
          <HeroHeader
            langFile={langFile}
            //@ts-ignore
            data={section}
          />
        );

      case "feature":
        return (
          <Feature
            //@ts-ignore
            data={section}
            langFile={langFile}
          />
        );
      case "heading":
        return (
          <CentreCtaSection
            title={[{ text: section?.heading as string, highlight: false }]}
            description={section?.subHeading as string}
            gradient={true}
            ctas={[
              {
                type: ButtonTypeEnum.Primary,
                size: ButtonSizeEnum.Regular,
                text: "Request a demo",
                actionType: CtaActionType.BookDemo,
              },
            ]}
          />
        );
      case "results":
        return (
          <ResultsSection
            title={section?.heading}
            results={[{ text: section?.results as string, highlight: true }]}
            title2={section?.title2}
            gradient={true}
          />
        );
      case "marquee":
        return (
          <MarqueeCarousel pauseOnHover={true} pointer={true}>
            {section.list.map((item: { [key: string]: string }) => {
              return (
                <div
                  className="hover:bottom-shadow-purple-xl mx-6 flex h-10 w-max cursor-pointer items-start gap-3 rounded-xl border border-grey-2 px-3 py-2 hover:border-purple-2"
                  key={item.text}
                >
                  <div className="">{item.emoji}</div>
                  <div className="text-body-16">{item.text}</div>
                </div>
              );
            })}
          </MarqueeCarousel>
        );
      case "comparison-features":
        //@ts-ignore
        return <ComparisonFeatures data={section} />;
      case "marquee-images":
        return (
          <>
            {section.heading && (
              <h3 className="font-obviously mb-12 text-center text-3xl font-bold leading-9 text-black">
                {section.heading}
              </h3>
            )}
            <MarqueeImages
              list={section.list as Array<string>}
              isFeatured={Boolean(section.heading)}
            />
            {section.footer && (
              <h3 className="mt-8 text-center leading-6 text-slate02">
                {section.footer}
              </h3>
            )}
          </>
        );
      case "testimonials":
        //@ts-ignore
        return <HomeTestimonials langFile={langFile} data={section} />;
      case "table":
        //@ts-ignore
        return <ComparisonTable langFile={langFile} data={section} />;
      case "reviews":
        //@ts-ignore
        return <ReviewsSlider langFile={langFile} data={section} />;
      case "multimedia-grid":
        //@ts-ignore
        return <MultimediaGrid data={section} />;
      case "features-grid":        
        return (
          <ErrorBoundary>
            <OtherOfferingsSection
              description={section.description}
              otherOfferingsMetaData={section.features}
              title={[{ text: section.heading }]}
              gap="gap-4"
            />
          </ErrorBoundary>
        );
        case "features-grid-us": 
        return (
          <ErrorBoundary>
            <OtherOfferingsSectionUS
              description={section.description}
              otherOfferingsMetaData={section.features}
              title={[{ text: section.heading }]}
              gap="gap-4"
              subTitle={section.subtitle}
              ctas={section.ctas}
            />
          </ErrorBoundary>
        );
      case "marketing-header":
        //@ts-ignore
        return <MarketingHeading langFile={langFile} data={section} />;
      case "marketing-header-us":
        //@ts-ignore
        return <MarketingHeadingUs langFile={langFile} data={section} />;
      case "creator-estimation":
        //@ts-ignore
        return <CreatorEstimationSection langFile={langFile} data={section} />;
        case "creator-estimation-us":
          //@ts-ignore
          return <CreatorEstimationSectionUS langFile={langFile} data={section} />;
      case "price-compare":
        //@ts-ignore
        return <PricingCompareSection langFile={langFile} data={section} />;
      case "level-up-section":
        //@ts-ignore
        return <LevelUpSection langFile={langFile} data={section} />;
      case "comparison-features-with-cta":
        //@ts-ignore
        return <ComparisonFeaturesWithCta langFile={langFile} data={section} />;
      case "plans-comparison-table":
        //@ts-ignore
        return <PlansComparisonTable langFile={langFile} data={section} />;
      case "promotion-strip":
        //@ts-ignore
        return <PromotionStrip data={section} />;
        case "promotion-strip-us":
        //@ts-ignore
        return <PromotionStripUS data={section} />;
      case "success-stories":
        //@ts-ignore
        return <SuccessStoriesMarqueeSection {...section} />;
      case "webinar-testimonial":
        //@ts-ignore
        return <WebinarTestimonial {...section.data} />;
        
      case "webinar-details":
        //@ts-ignore
      return <WebinarDetails data={section as WebinarDetailsProps} />;

      case "about-creator":
        return <AboutCreator {...section.data} />;

      case "certificate":
        return  <Certificate {...section.data} />

         

      
    

      case "webinar-intent":
        return <WebinarIntent {...section.data} />;

      case "creator-result":
        return <CreatorResult {...section.data} />;

      case "additional-bonus":
        return <AdditionalBonus {...section.data} />;

      case "roadmap":
        return <Roadmap {...section.data} />;

      case "productStepsSection":
        return <ProductStepsSection {...section.data} />;
      case "infoAccordionSection":
        return <InfoAccordion {...section.data} />;
      case "graphyAssist":
        return <GraphyAssist {...section.data} />;
      case "featureGrid":
        return <FeaturesGrid {...section.data} />;
      case "pricingPlans":
        return (
          <PricingComparisonSection {...section.data} />
        );
      case "scheduleDemo":
        return <ScheduleCallSection {...section.data} />;
      case "featureSection":
        return <FeatureSection {...section.sectionData} />;
      case "faqs":
        return (
          <>
            <div className="my-20">
              {/* TODO: Localize this: */}
              <FaqSection
                showCTA={handeShowCTA(section)}
                faqs={section.faqs}
                title={section.heading as string}
                cta={{
                  type: ButtonTypeEnum.Primary,
                  size: ButtonSizeEnum.Regular,
                  text: "Visit help centre",
                  href: FAQ_HREF,
                  target: "_blank",
                  amplitudeEvent: {
                    eventName:
                      LANDING_PAGE_EVENTS.VISIT_HELP_CENTER_TAPPED.LABEL,
                    eventProperties: {
                      source: "faqs",
                    },
                  },
                }}
                description={
                  section?.description ??
                  "Explore our help center for detailed information, troubleshooting guides, and more"
                }
              />
            </div>
          </>
        );
      case "spacing":
        return <div className={section.spacingClass as string}></div>;
      default:
        return null;
    }
  }

  const { animatedLPStyleSheets } = useThemeStore();

  const orgStructuredData = {
    "@context": "http://schema.org",
    "@type": "Organization",
    name: "Graphy",
    description:
      "Graphy is a platform that empowers creators and educators to scale their online brand and business by launching their online courses and sell them via websites and mobile apps.",
    image: "https://graphy.com/images/graphy-brand-logo.svg",
    logo: "https://graphy.com/images/graphy-brand-logo.svg",
    url: "https://graphy.com/store",
    telephone: "+91 9513165622 ",
    sameAs: [
      "https://twitter.com/graphyapp",
      "https://www.linkedin.com/company/graphyapp",
      "https://www.youtube.com/c/GraphybyUnacademy",
      "https://www.instagram.com/graphyapp/",
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress:
        "Graphy  Park Plaza - XJJP+6R9, Indira Nagar II Stage,  Hoysala Nagar, Indiranagar",
      addressLocality: "Bengaluru",
      postalCode: "560038",
      addressCountry: "India",
    },
  };


  

  const firstpromoterScript  = ()=>{
    let cid = FIRST_PROMOTER_KEY;
    if(isUsLocale){
      cid = FIRST_PROMOTER_KEY_US;
    }
    return (`(function(){var t=document.createElement("script");
    t.type="text/javascript",t.async=!0,t.src='https://cdn.firstpromoter.com/fprom.js',t.onload=t.onreadystatechange=function(){var t=this.readyState;if(!t||"complete"==t||"loaded"==t)try{$FPROM.init("${cid}",".${ROOT_HOST}")}catch(t){}};var e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(t,e)})();`);
  }
  
  useEffect(() => {
    if (isthinkfic && shouldShowExitIntent) {
      setShowExitModal(true);
    }
  }, [isthinkfic, shouldShowExitIntent]);

  useEffect(() => {
    pageSections.forEach((section: { [key: string]: any }) => {
      if (section.type === "about-creator") {
        setCourseInfo((prevInfo) => ({
          ...prevInfo,
          instructorImage: section.data?.imageUrl
        }));
      } else if (section.type === "marketing-header-us") {
        setCourseInfo((prevInfo) => ({
          ...prevInfo,
          title: section.heading
        }));
      } else if (section.type === "creator-estimation-us") {
        setCourseInfo((prevInfo) => ({
          ...prevInfo,
          date: section.list[0].heading,
          time: section.list[1].heading,
          duration: section.list[2].heading,
          language: section.list[3].heading
        }));
      }
    });
  }, [pageSections]);



  
  return (
    <>
      <MainLayout
        hideNavCta
        navbarData={navbarData}
        footerData={footerData}
        showFooter={isWebinarSuccess ? false : true}
      >
        <Head>
          <meta charSet="UTF-8" />
          <title>{title}</title>
          {(langFile === PAGES.AFFILIATE_PAGE ||
            (langFile === PAGES.MEMBERSHIP_PAGE && locale === "us")) && (
            <meta name="robots" content="noindex" />
          )}
          <link rel="canonical" href={`https://${ROOT_HOST}${router.asPath}`} />
          <meta name="description" content={description} />
          <meta property="og:title" content={title} key="ogtitle" />
          <meta property="og:description" content={description} key="ogdesc" />
          <link rel="stylesheet" href={animatedLPStyleSheets} />
          <script
            dangerouslySetInnerHTML={{
              __html: firstpromoterScript(),
            }}
          />
          {noIndexMeta && <meta name="robots" content="noindex" />}
          <meta
            property="image"
            content="https://graphy.com/images/OG-image.png"
          />
          <meta
            property="og:image"
            content={ogImage || `https://graphy.com/images/OG-image.png`}
          />
          <meta
            name="twitter:image"
            content={`https://graphy.com/images/graphy-logo-icon.jpg`}
          />
          <script
            key="GraphyOrgSchema"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(orgStructuredData),
            }}
          />
          {router.asPath.includes("launch-course-website-mobile-app") &&
            RatingSchema()}
        </Head>
        <main className="flex-grow sm:px-0">
          {isthinkfic && (
            <ExitIntentForm
              showModal={showExitModal}
              onClose={() => setShowExitModal(false)}
              onSuccess={() => setShowExitModal(false)}
            />
          )}
        {renderLPBody()}
          {pageType === "marketing" && (
            <HeroHeader
              className=""
              //@ts-ignore
              data={{
                type: "hero-header",
                title: marketingFooterTitle,
                footer: true,
                defaultCountry:
                  pageSections?.[1]?.defaultCountry &&
                  pageSections?.[1]?.defaultCountry !== "in"
                    ? pageSections?.[1]?.defaultCountry
                    : "us",
                emailOnly: pageSections?.[1]?.emailOnly
                  ? pageSections?.[1]?.emailOnly
                  : true, // this affects Validation inside LeadGenerationInputs --> true: validates only email, false: mobile validation is enabled as well
                validateMobile:
                  pageSections?.[1]?.validateMobile === false
                    ? pageSections?.[1]?.validateMobile
                    : true,
                subTitle:
                  marketFooterSubtitle ||
                  "Create custom courses, build beautiful no-code websites, \nor launch your own app — all using just Graphy.",
                ctaType: "lead-generation",
                ctaText: marketFooterCtaText || "Request a demo",
                gradientColor: "aurora3",
                amplitudeSrc: "title",
                headerType: "image",
                heroImage: marketingFooterHero || "/images/hero-header.png",
                ctaPromoText: marketFooterCtaPromoText || null,
              }}
              handleLaunch={() => {}}
            />
          )}
          {pageType === "marketing-us" && (
            <HeroHeader
              className=""
              //@ts-ignore
              data={{
                type: "hero-header",
                title: marketingFooterTitle,
                footer: true,
                defaultCountry:
                  pageSections?.[1]?.defaultCountry &&
                  pageSections?.[1]?.defaultCountry !== "in"
                    ? pageSections?.[1]?.defaultCountry
                    : "us",
                emailOnly: pageSections?.[1]?.emailOnly
                  ? pageSections?.[1]?.emailOnly
                  : true, // this affects Validation inside LeadGenerationInputs --> true: validates only email, false: mobile validation is enabled as well
                validateMobile:
                  pageSections?.[1]?.validateMobile === false
                    ? pageSections?.[1]?.validateMobile
                    : true,
                subTitle:
                  marketFooterSubtitle ||
                  "Create custom courses, build beautiful no-code websites, \nor launch your own app — all using just Graphy.",
                ctaType: "lead-generation-us",
                ctaText: marketFooterCtaText || "Request a demo",
                gradientColor: "aurora3",
                amplitudeSrc: "title",
                headerType: "image",
                heroImage: marketingFooterHero || "/images/hero-header.png",
                ctaPromoText: marketFooterCtaPromoText || null,
              }}
              handleLaunch={() => {}}
            />
          )}
          {pageType === "affiliate-us" && (
            <HeroHeader
              className=""
              //@ts-ignore
              data={{
                type: "hero-header",
                title:"",
                footer: true,
                defaultCountry:
                  pageSections?.[1]?.defaultCountry &&
                  pageSections?.[1]?.defaultCountry !== "in"
                    ? pageSections?.[1]?.defaultCountry
                    : "us",
                emailOnly: pageSections?.[1]?.emailOnly
                  ? pageSections?.[1]?.emailOnly
                  : true, // this affects Validation inside LeadGenerationInputs --> true: validates only email, false: mobile validation is enabled as well
                validateMobile:
                  pageSections?.[1]?.validateMobile === false
                    ? pageSections?.[1]?.validateMobile
                    : true,
                subTitle:"",
                ctaType: "affiliate-us",
                ctaText: marketFooterCtaText || "Request a demo",
                gradientColor: "aurora3",
                amplitudeSrc: "title",
                headerType: "affiliate-image",
                heroImage: "",
                ctaPromoText: marketFooterCtaPromoText || null,
              }}
              handleLaunch={() => {}}
            />
          )}
        </main>
      </MainLayout>
    </>
  );
};

export default CreateAnimatedLP;
